import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import type { LogLevel } from '@common/logging/logging.service';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface AppConfig {
  API_URL: string;
  PRODUCTION: boolean;
  SENTRY_RELEASE: string | undefined;
  SENTRY_SAMPLE_RATE: number;
  SENTRY_TRACES_SAMPLE_RATE: number;
  SENTRY_ENVIRONMENT: string | undefined;
  SENTRY_LOG_LEVEL: string;
  LOGGER_LOG_LEVEL: LogLevel;
  DCMS_VERSION: string | undefined;
  DCMS_BRANCH: string | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  readonly #httpHandler = inject(HttpBackend);

  readonly #http: HttpClient;
  readonly #configuration = signal<AppConfig>({
    API_URL: '',
    DCMS_BRANCH: undefined,
    DCMS_VERSION: undefined,
    LOGGER_LOG_LEVEL: 'off',
    PRODUCTION: true,
    SENTRY_ENVIRONMENT: undefined,
    SENTRY_LOG_LEVEL: 'off',
    SENTRY_RELEASE: undefined,
    SENTRY_SAMPLE_RATE: 0,
    SENTRY_TRACES_SAMPLE_RATE: 0,
  });

  constructor() {
    this.#http = new HttpClient(this.#httpHandler);
  }

  setConfig(): Promise<void | AppConfig> {
    const path =
      environment.env === 'development'
        ? '/app-config.json'
        : '/v2/app-config.json';

    // Revalidate: a cached copy from an earlier release would mix old config with new bundles.
    return firstValueFrom(
      this.#http.get<AppConfig>(path, { cache: 'no-cache' })
    )
      .then((config: AppConfig) => {
        this.#configuration.set({ ...this.#configuration(), ...config });
        return this.#configuration();
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Error loading app config:', error);
      });
  }

  readConfig(): AppConfig {
    return this.#configuration();
  }
}
